import { Option, number, Widget } from "widgets-for-react";
import { RouteParams, CustomRouteParams } from "./router_state";
import { State, ExtraUserInfo } from "../portal_client";

export type SessionSetter<extra_user_info> = (user: User<extra_user_info>|undefined, session: SessionId|undefined, role: string|undefined) => void
export type SessionGetter<extra_user_info> = () => Option<UserSession<extra_user_info>>

export interface UserSession<extra_user_info> {
  readonly user: User<extra_user_info>
  readonly session: SessionId
  readonly role: string | undefined
}

export type Role = string

export interface BasicCredentials { username:string, password:string }
export interface LoginData extends BasicCredentials { show_password:boolean, role?:string, email?: string }

export const emptyLoginData: LoginData = { username: "", password: "", show_password: false}

export interface ChooseRoleData extends BasicCredentials { role?:string, roles:string[], pin?: string }

export interface LoginDataTwoFactorData extends BasicCredentials { pin:string }

export interface ChangePasswordData { old_password: string|undefined,
                                      new_password:string|undefined,
                                      new_password_confirmed:string|undefined,
                                      show_password: boolean }

export interface SignUpData { username:string|undefined
                              password:string|undefined
                              confirmed_password:string|undefined,
                              email:string|undefined,
                              confirmed_email:string|undefined,
                              name:string|undefined,
                              surname:string|undefined }

export interface ResetPasswordSuccessData extends BasicCredentials {}
export interface SignupSuccessData extends BasicCredentials {}

export interface ResetPasswordData { password:string|undefined,
                                     email:string|undefined,
                                     confirmed_password:string|undefined,
                                     reset_password_token:string|undefined,
                                     show_password: boolean }

export interface VerifyResetPasswordTokenData { reset_password_token:string|undefined, email:string|undefined }

export interface ActivateAccountData { token:string|undefined, email:string|undefined }

export interface ResetPasswordRequestData { email:string|undefined }

export interface ResendActivationData { email:string|undefined }

export interface PasswordKind { kind:"standard" | "two factor" }

export type Token = string



export type User<extra_user_info>  =
  { username: string
    email: string
    name?: string
    surname?: string } 
  & extra_user_info

export type SessionId = string

export type PrivacyKind = "standard"|"strict"

export interface LoggedInState<extra_user_info> {
  readonly status: "logged in"
                 | "logged in - activation success"
                 | "logged in - signup success"
                 | "logged in - logout api error"
                 | "logged in - logging out",
  readonly user:User<extra_user_info>,
  readonly role:Role,
  readonly session:SessionId,
}

export function isLoggedInState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is LoggedInState<extra_user_info> {
  return s.status == "logged in" ||
         s.status == "logged in - activation success" ||
         s.status == "logged in - signup success" ||
         s.status == "logged in - logout api error" ||
         s.status == "logged in - logging out"
}

export interface ChangePasswordState<extra_user_info> {
  readonly status: "change password"
                 | "change password - sending"
                 | "change password - api error"
                 | "change password - success"
                 | "change password - error"
                 | "change password - error missing fields"
                 | "change password - error incorrect password"
                 | "change password - error too weak password"
                 | "change password - error passwords do not match"
  readonly user:User<extra_user_info>
  readonly change_password_data: ChangePasswordData
  readonly session:SessionId
  readonly role:Role
}

export function isChangePasswordState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ChangePasswordState<extra_user_info>  {
  return s.status == "change password" ||
         s.status == "change password - sending" ||
         s.status == "change password - api error" ||
         s.status == "change password - success" ||
         s.status == "change password - error" ||
         s.status == "change password - error missing fields" ||
         s.status == "change password - error incorrect password" ||
         s.status == "change password - error too weak password" ||
         s.status == "change password - error passwords do not match"
}

export interface LoggedOutState {
  readonly status: "logged out"
                 | "logged out - logging in"
                 | "logged out - login api error"
                 | "logged out - error login not authorized"
                 | "logged out - error account already active"
                 | "logged out - error account not active"
                 | "logged out - error too many reset password attempts"
                 | "logged out - error reset password token has expired"
                 | "logged out - error reset password token not found"
                 | "logged out - error too many account activation attempts"
                 | "logged out - error account activation token not found"
                 | "logged out - error too many attempts",
  readonly login_data:LoginData
}

export function isLoggedOutState<a,extra_user_info>(s: LoginState<a, extra_user_info>): s is LoggedOutState  {
  return s.status == "logged out" ||
         s.status == "logged out - logging in" ||
         s.status == "logged out - login api error" ||
         s.status == "logged out - error login not authorized" ||
         s.status == "logged out - error too many attempts" ||
         s.status == "logged out - error too many reset password attempts" ||
         s.status == "logged out - error reset password token has expired" ||
         s.status == "logged out - error reset password token not found" ||
         s.status == "logged out - error too many account activation attempts" ||
         s.status == "logged out - error account activation token not found" ||
         s.status == "logged out - error account already active" ||
         s.status == "logged out - error account not active"
}


export interface LoginTwoFactorState {
  readonly status: "two factor"
                 | "two factor - logging"
                 | "two factor - error wrong code"
                 | "two factor - error empty code"
                 | "two factor - login api error"
                 | "two factor - resend api error"
                 | "two factor - resending pin"
                 | "two factor - error too many attempts",
  readonly login_data:LoginDataTwoFactorData
}


export function isLoginTwoFactorState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is LoginTwoFactorState  {
  return s.status == "two factor" ||
         s.status == "two factor - logging" ||
         s.status == "two factor - error empty code" ||
         s.status == "two factor - error wrong code" ||
         s.status == "two factor - resending pin" ||
         s.status == "two factor - resend api error" ||
         s.status == "two factor - login api error"
}

export interface SignUpState {
  readonly status: "sign up"
                 | "sign up - success"
                 | "sign up - signing up"
                 | "sign up - api error"
                 | "sign up - error"
                 | "sign up - error invalid email"
                 | "sign up - error weak password"
                 | "sign up - error invalid username"
                 | "sign up - error account not active"
                 | "sign up - error account already exists"
                 | "sign up - error too many attempts"
  readonly login_data:SignUpData
}


export function isSignUpState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is SignUpState  {
  return s.status == "sign up" ||
         s.status == "sign up - success" ||
         s.status == "sign up - api error" ||
         s.status == "sign up - error" ||
         s.status == "sign up - error invalid email" ||
         s.status == "sign up - error invalid username" ||
         s.status == "sign up - error account not active" ||
         s.status == "sign up - error account already exists" ||
         s.status == "sign up - error too many attempts" ||
         s.status == "sign up - error weak password"
}

export interface ResendActivationState {
  readonly status: "resend activation"
                 | "resend activation - success"
                 | "resend activation - api error"
                 | "resend activation - resending"
                 | "resend activation - error token expired"
                 | "resend activation - error too many attempts"
  readonly login_data:ResendActivationData
}

export function isResendActivationState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ResendActivationState  {
  return s.status == "resend activation" ||
         s.status == "resend activation - success" ||
         s.status == "resend activation - api error" ||
         s.status == "resend activation - resending" ||
         s.status == "resend activation - error token expired" ||
         s.status == "resend activation - error too many attempts"
}

export interface ResetPasswordState {
  readonly status: "reset password"
                 | "reset password - resetting password"
                 | "reset password - api error"
                 | "reset password - error weak password"
                 | "reset password - error too many attempts"
  readonly login_data:ResetPasswordData
}

export function isResetPasswordState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ResetPasswordState  {
  return s.status == "reset password" ||
         s.status == "reset password - resetting password" ||
         s.status == "reset password - api error" ||
         s.status == "reset password - error weak password" ||
         s.status == "reset password - error too many attempts"
}
export interface ResetPasswordSuccessState {
  readonly status: "reset password success"
                 | "reset password success - logging in"
                 | "reset password success - api error"
  readonly login_data:ResetPasswordSuccessData
}

export function isResetPasswordSuccessState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ResetPasswordSuccessState {
  return s.status == "reset password success" ||
         s.status == "reset password success - logging in" ||
         s.status == "reset password success - api error"
}

export interface VerifyResetPasswordTokenState {
  readonly status: "verify reset password token"
                 | "verify reset password token - sending"
                 | "verify reset password token - api error"
  readonly login_data:VerifyResetPasswordTokenData
}

export function isVerifyResetPasswordTokenState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is VerifyResetPasswordTokenState {
  return s.status ==  "verify reset password token" ||
         s.status ==  "verify reset password token - sending" ||
         s.status ==  "verify reset password token - api error"
}

export interface ActivateAccountState {
  readonly status: "activate account"
                 | "activate account - activating"
                 | "activate account - api error"
                 | "activate account - error token not found"
  readonly login_data:ActivateAccountData
}

export function isActivateAccountState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ActivateAccountState  {
  return s.status == "activate account" ||
         s.status == "activate account - activating" ||
         s.status == "activate account - api error" ||
         s.status == "activate account - error token not found"
}

export interface ResetPasswordRequestState {
  readonly status: "reset password request"
                 | "reset password request - success"
                 | "reset password request - resetting"
                 | "reset password request - api error"
                 | "reset password request - error too many attempts"
                 | "reset password request - error user not activated"
                 | "reset password request - error email not valid"
                 | "reset password request - error email not found"
  readonly login_data:ResetPasswordRequestData
}

export function isResetPasswordRequestState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ResetPasswordRequestState  {
  return s.status == "reset password request" ||
         s.status == "reset password request - success" ||
         s.status == "reset password request - resetting" ||
         s.status == "reset password request - api error" ||
         s.status == "reset password request - error too many attempts" ||
         s.status == "reset password request - error user not activated" ||
         s.status == "reset password request - error email not valid" ||
         s.status == "reset password request - error email not found"
}

export interface ChooseRoleState {
  readonly status: "choose role"
                 | "choose role - logging in"
                 | "choose role - api error"
                 | "choose role - error too many attempts"
                 | "choose role - error no role selected"
                 | "choose role - error incompatible role for user"
  readonly login_data:ChooseRoleData
}

export function isChooseRoleState<a, extra_user_info>(s: LoginState<a, extra_user_info>): s is ChooseRoleState {
  return s.status == "choose role" ||
         s.status == "choose role - logging in" ||
         s.status == "choose role - api error" ||
         s.status == "choose role - error too many attempts" ||
         s.status == "choose role - error incompatible role for user" ||
         s.status == "choose role - error no role selected"
}
export interface Page404 {
  readonly status: "404"
}
export type LoginState<a, extra_user_info> = 
                           (LoggedInState<extra_user_info>
                            | LoggedOutState
                            | SignUpState
                            | ChangePasswordState<extra_user_info>
                            | ResetPasswordState
                            | ResetPasswordRequestState
                            | ActivateAccountState
                            | LoginTwoFactorState
                            | ChooseRoleState
                            | ResendActivationState
                            | VerifyResetPasswordTokenState
                            | ResetPasswordSuccessState
                            | Page404) & { readonly appState?: a }

export type AppState<a, b extends {kind:string}, extra_user_info> = { login : LoginState<a, extra_user_info>, privacy_kind:"standard"|"strict", get_initial_state:() => a, route: CustomRouteParams<b>, get_404_page:Option<Widget<never>> }

